import { LitElement, html, css, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { CONFIDENCE_TIERS } from '../lib/palette.js';
import { EXCLUSION_WORDS, PROJECT_LIST_LIMIT, projectListRows } from '../lib/project.js';
import { hours as formatHours, count, humanize } from '../lib/format.js';

const TIER_META = new Map(CONFIDENCE_TIERS.map((t) => [t.key, t]));
const SEARCH_DELAY_MS = 250;

/**
 * The project picker for Project Intelligence (Phase 31): every project the run in the filter
 * bar saw, largest allocation first, searchable by number, name or client, each row a link to
 * its panel. It reads `GET /forecast/runs/:id/projects` — the Phase 8 list, unchanged — and shows
 * the facts a planner picks by: how the hours were sourced, the tier, what remains, what is in
 * the horizon, and whether the run slipped or flagged it. Excluded projects stay in the list and
 * say why, because "why is this project not in the forecast" is a question the panel answers.
 */
class ProjectList extends StoreMixin(LitElement) {
  static properties = {
    _rows: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _q: { state: true },
    _includedOnly: { state: true },
    _offset: { state: true },
  };

  constructor() {
    super();
    this._rows = null;
    this._loading = false;
    this._error = null;
    this._q = '';
    this._includedOnly = false;
    this._offset = 0;
    this._requestKey = null;
    this._searchTimer = null;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 6px 18px;
      margin-bottom: 12px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 2px 0 0;
      font-size: 20px;
      line-height: 1.2;
      color: var(--ink-strong);
    }
    .sub {
      color: var(--ink-muted);
      font-size: 12.5px;
    }
    .controls {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
      margin: 4px 0 12px;
    }
    input[type='search'] {
      font: inherit;
      font-size: 13px;
      color: var(--ink);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
      border-radius: var(--radius-sm);
      padding: 6px 10px;
      min-width: 240px;
    }
    label.check {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .list {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12.5px;
    }
    th,
    td {
      padding: 6px 10px;
      text-align: left;
      border-top: 1px solid var(--border-soft);
      white-space: nowrap;
    }
    th {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--ink-muted);
      border-top: 0;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    td.name {
      white-space: normal;
      min-width: 220px;
    }
    td.name .number {
      font-family: var(--font-eyebrow);
      font-size: 11px;
      color: var(--ink-muted);
      margin-right: 6px;
    }
    td.name .client {
      display: block;
      color: var(--ink-muted);
      font-size: 11.5px;
    }
    tr.excluded td {
      color: var(--ink-muted);
    }
    button.open {
      font: inherit;
      font-weight: 600;
      color: var(--link);
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      text-align: left;
    }
    button.open:hover {
      text-decoration: underline;
    }
    .marker {
      font-family: var(--font-eyebrow);
      color: var(--ink-muted);
      margin-right: 4px;
    }
    .state {
      padding: 28px 0;
      color: var(--ink-muted);
      font-size: 13px;
    }
    .state.error {
      color: var(--status-overloaded-ink);
    }
    .pager {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 10px;
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .pager button {
      font: inherit;
      font-size: 12.5px;
      color: var(--ink);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      padding: 3px 12px;
      cursor: pointer;
    }
    .pager button[disabled] {
      opacity: 0.5;
      cursor: default;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
    clearTimeout(this._searchTimer);
  }

  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    return {
      run: filters.run ?? 'latest',
      q: this._q.trim() || null,
      included: this._includedOnly ? true : null,
      limit: PROJECT_LIST_LIMIT,
      offset: this._offset,
    };
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._rows = null;
      return;
    }
    const { run, ...query } = params;
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get(`/forecast/runs/${run}/projects`, query);
      if (this._requestKey !== key) return;
      this._rows = projectListRows(data.projects);
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._rows = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  _onSearch(value) {
    this._q = value;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this._offset = 0;
      this._maybeFetch();
    }, SEARCH_DELAY_MS);
  }

  _toggleIncluded(checked) {
    this._includedOnly = checked;
    this._offset = 0;
    this._maybeFetch();
  }

  _page(delta) {
    this._offset = Math.max(0, this._offset + delta * PROJECT_LIST_LIMIT);
    this._maybeFetch();
  }

  _open(row) {
    navigate(`/projects/${row.project_id}`);
  }

  render() {
    const { run } = this.appState;
    const rows = this._rows;
    let body;
    if (!run) body = html`<div class="state">No forecast run yet — no projects to pick from.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!rows && this._loading)
      body = html`<div class="state">Reading the run's projects…</div>`;
    else if (!rows || rows.length === 0)
      body = html`<div class="state" data-state="empty">
        ${this._q ? `Nothing matches "${this._q}" in this run.` : 'The run saw no projects.'}
      </div>`;
    else
      body = html`
        <div class="list">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Type</th>
                <th>Source</th>
                <th>Tier</th>
                <th class="num">Remaining</th>
                <th class="num">In horizon</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${repeat(
                rows,
                (r) => r.project_id,
                (r) => {
                  const tier = TIER_META.get(r.confidence_tier);
                  const notes = [
                    !r.included
                      ? `excluded: ${EXCLUSION_WORDS[r.exclusion_reason] ?? r.exclusion_reason}`
                      : null,
                    r.slipped ? 'slipped' : null,
                    r.burn_risk ? 'burn risk' : null,
                    r.flags > 0 ? `${count(r.flags)} ${r.flags === 1 ? 'flag' : 'flags'}` : null,
                  ].filter(Boolean);
                  return html`
                    <tr class=${r.included ? '' : 'excluded'} data-project=${r.project_id}>
                      <td class="name">
                        <button class="open" @click=${() => this._open(r)}>
                          <span class="number">${r.number ?? ''}</span> ${r.name}
                        </button>
                        <span class="client">${r.client ?? '—'}</span>
                      </td>
                      <td>${r.canonical_type ? humanize(r.canonical_type) : '—'}</td>
                      <td>${r.source_label}</td>
                      <td>
                        ${
                          tier
                            ? html`${tier.marker ? html`<span class="marker">${tier.marker}</span>` : nothing}${tier.label}`
                            : '—'
                        }
                      </td>
                      <td class="num">${formatHours(r.hours_remaining)}</td>
                      <td class="num">${formatHours(r.hours_allocated)}</td>
                      <td>${notes.join(' · ') || nothing}</td>
                    </tr>
                  `;
                },
              )}
            </tbody>
          </table>
        </div>
        <div class="pager">
          <button ?disabled=${this._offset === 0} @click=${() => this._page(-1)}>Previous</button>
          <span>
            ${count(this._offset + 1)}–${count(this._offset + rows.length)} · largest allocation
            first
          </span>
          <button ?disabled=${rows.length < PROJECT_LIST_LIMIT} @click=${() => this._page(1)}>
            Next
          </button>
        </div>
      `;

    return html`
      <div class="card">
        <div class="head">
          <div>
            <div class="eyebrow">Project intelligence</div>
            <h2>Where did this number come from?</h2>
            <div class="sub">
              Pick a project to see its source mode, remaining hours, schedule, roles, analogs,
              flags and linked deal for the run in the filter bar.
            </div>
          </div>
        </div>
        <div class="controls">
          <label class="field">
            <span class="sr-only"></span>
            <input
              type="search"
              placeholder="Number, name or client…"
              aria-label="Search projects"
              .value=${this._q}
              @input=${(e) => this._onSearch(e.target.value)}
            />
          </label>
          <label class="check">
            <input
              type="checkbox"
              .checked=${this._includedOnly}
              @change=${(e) => this._toggleIncluded(e.target.checked)}
            />
            In the forecast only
          </label>
        </div>
        ${body}
      </div>
    `;
  }
}

customElements.define('project-list', ProjectList);
